import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Bell,
  Shield,
  Database,
  LogOut,
  ChevronRight,
  Sparkles,
  Mic,
  MessageSquare,
  Zap
} from 'lucide-react';
import EditProfileModal from '../mobile/EditProfileModal';
import { supabase } from '@/lib/supabase';

/**
 * SettingsView - Premium control center with iOS aesthetic and cyberpunk edge
 * @param {object} user - Current authenticated user from Supabase
 * @param {object} profile - User profile data
 * @param {function} onProfileUpdate - Callback when profile is updated
 */
export default function SettingsView({ user, profile, onProfileUpdate }) {
  const [selectedPersonality, setSelectedPersonality] = useState('velvet');
  const [wakeWordEnabled, setWakeWordEnabled] = useState(true);
  const [responseLength, setResponseLength] = useState(60); // 0-100
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(profile);

  // AI Noise Cancellation toggle - syncs with VoiceVideoTab via localStorage
  // Default OFF on mobile to avoid 23MB WASM download
  const [aiNoiseEnabled, setAiNoiseEnabled] = useState(() => {
    const saved = localStorage.getItem('gjyl_noise_suppression');
    return saved === 'true'; // Default false if not set
  });
  const [aiNoiseLoading, setAiNoiseLoading] = useState(false);

  // Save AI noise setting to localStorage
  useEffect(() => {
    localStorage.setItem('gjyl_noise_suppression', String(aiNoiseEnabled));
  }, [aiNoiseEnabled]);

  // Handle AI noise toggle with loading state for download
  const handleAiNoiseToggle = async (enabled) => {
    if (enabled) {
      // Show loading while DFN3 downloads
      setAiNoiseLoading(true);
      try {
        // Preload DFN3 module (23MB WASM) so it's ready for calls
        await import('../../dfn3/main-v2');
        setAiNoiseEnabled(true);
      } catch (err) {
        console.warn('[SettingsView] Failed to load AI noise suppression:', err);
        // Still enable - will retry on actual use
        setAiNoiseEnabled(true);
      } finally {
        setAiNoiseLoading(false);
      }
    } else {
      setAiNoiseEnabled(false);
    }
  };

  const handleProfileUpdate = (updatedProfile) => {
    setCurrentProfile(updatedProfile);
    if (onProfileUpdate) {
      onProfileUpdate(updatedProfile);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // Use currentProfile if available, otherwise fallback to prop
  const displayProfile = currentProfile || profile;

  const personalities = [
    {
      id: 'assistant',
      name: 'Assistant',
      description: 'Helpful & Polite',
      color: 'from-blue-500 to-cyan-500',
      icon: '🤖'
    },
    {
      id: 'bestie',
      name: 'Bestie',
      description: 'Casual & Fun',
      color: 'from-pink-500 to-rose-500',
      icon: '😊'
    },
    {
      id: 'velvet',
      name: 'Velvet',
      description: 'Sassy & Mysterious',
      color: 'from-purple-500 to-indigo-500',
      icon: '✨'
    }
  ];

  return (
    <motion.div
      className="h-[calc(100vh-64px)] w-full bg-gjyl-darker overflow-y-auto pb-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-gjyl-darker/80 backdrop-blur-xl border-b border-white/5">
        <div className="px-6 pt-12 pb-4">
          <h1 className="text-4xl font-bold text-white">Settings</h1>
        </div>
      </div>

      {/* Identity Section */}
      <div className="px-6 py-8">
        <div className="flex flex-col items-center text-center">
          {/* Avatar */}
          <div className="relative mb-4">
            {displayProfile?.avatar_url ? (
              <motion.img
                src={displayProfile.avatar_url}
                alt={displayProfile?.username || 'User'}
                className="w-24 h-24 rounded-full object-cover"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              />
            ) : (
              <motion.div
                className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center text-white text-3xl font-bold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {(displayProfile?.username || user?.email || 'U').charAt(0).toUpperCase()}
              </motion.div>
            )}
            {/* Pro Badge */}
            <motion.div
              className="absolute -bottom-1 -right-1 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full px-2 py-0.5 text-xs font-bold text-black"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              PRO
            </motion.div>
          </div>

          {/* Name and Handle */}
          <h2 className="text-xl font-bold text-white mb-1">{displayProfile?.username || displayProfile?.display_name || 'User'}</h2>
          <p className="text-gray-400 text-sm mb-4">@{displayProfile?.username?.toLowerCase() || 'user'}</p>

          {/* Edit Profile Button */}
          <motion.button
            className="px-6 py-2 border border-white/20 rounded-full text-white text-sm font-medium hover:bg-white/5"
            onClick={() => setEditProfileOpen(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Edit Profile
          </motion.button>
        </div>
      </div>

      {/* Velvet Neural Interface Section */}
      <div className="px-6 py-6">
        <SectionHeader
          icon={<Sparkles className="w-5 h-5 text-purple-400" />}
          title="Velvet Neural Interface"
          subtitle="Customize your AI companion"
        />

        {/* Personality Selector */}
        <div className="mb-6">
          <label className="text-white text-sm font-medium mb-3 block">
            Personality
          </label>
          <div className="flex space-x-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {personalities.map((personality) => (
              <PersonalityCard
                key={personality.id}
                personality={personality}
                isSelected={selectedPersonality === personality.id}
                onSelect={() => setSelectedPersonality(personality.id)}
              />
            ))}
          </div>
        </div>

        {/* Wake Word Toggle */}
        <SettingToggle
          icon={<Mic className="w-5 h-5" />}
          label="Wake Word"
          description="Activate with 'Hey Velvet'"
          enabled={wakeWordEnabled}
          onChange={setWakeWordEnabled}
        />

        {/* AI Noise Cancellation Toggle */}
        <div className="mt-3">
          <SettingToggle
            icon={<Zap className="w-5 h-5" />}
            label={aiNoiseLoading ? "Downloading AI..." : "AI Noise Cancellation"}
            description={aiNoiseLoading ? "23MB download in progress" : "Remove background noise from calls"}
            enabled={aiNoiseEnabled}
            onChange={handleAiNoiseToggle}
            loading={aiNoiseLoading}
            accentColor="emerald"
          />
          {!aiNoiseEnabled && (
            <p className="text-gray-500 text-xs mt-2 px-4">
              Uses WebRTC noise suppression when off (no download required)
            </p>
          )}
        </div>

        {/* Response Length Slider */}
        <div className="mt-6">
          <label className="text-white text-sm font-medium mb-3 flex items-center">
            <MessageSquare className="w-4 h-4 mr-2" />
            Response Length
          </label>
          <div className="flex items-center space-x-3">
            <span className="text-gray-400 text-xs">Short</span>
            <input
              type="range"
              min="0"
              max="100"
              value={responseLength}
              onChange={(e) => setResponseLength(parseInt(e.target.value))}
              className="flex-1 h-2 bg-zinc-800 rounded-full appearance-none cursor-pointer accent-purple-500"
              style={{
                background: `linear-gradient(to right, rgb(168, 85, 247) 0%, rgb(168, 85, 247) ${responseLength}%, rgb(39, 39, 42) ${responseLength}%, rgb(39, 39, 42) 100%)`
              }}
            />
            <span className="text-gray-400 text-xs">Detailed</span>
          </div>
        </div>
      </div>

      {/* System Section */}
      <div className="px-6 py-6">
        <SectionHeader
          title="System"
        />

        <div className="space-y-1">
          <SettingItem
            icon={<Bell className="w-5 h-5" />}
            label="Notifications"
            hasChevron
          />
          <SettingItem
            icon={<Shield className="w-5 h-5" />}
            label="Privacy"
            hasChevron
          />
          <SettingItem
            icon={<Database className="w-5 h-5" />}
            label="Data Usage"
            hasChevron
          />
          <SettingItem
            icon={<LogOut className="w-5 h-5" />}
            label="Logout"
            isDestructive
            onClick={handleLogout}
          />
        </div>
      </div>

      {/* Version Info */}
      <div className="px-6 py-8 text-center">
        <p className="text-gray-600 text-xs">GJYL v1.0.0</p>
        <p className="text-gray-700 text-xs mt-1">Built with 💜 by PaulyBear</p>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        user={user}
        profile={displayProfile}
        onProfileUpdate={handleProfileUpdate}
      />
    </motion.div>
  );
}

/**
 * SectionHeader - Section title component
 */
function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center space-x-2 mb-1">
        {icon}
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
      {subtitle && (
        <p className="text-gray-400 text-xs">{subtitle}</p>
      )}
    </div>
  );
}

/**
 * PersonalityCard - Personality selector card
 */
function PersonalityCard({ personality, isSelected, onSelect }) {
  return (
    <motion.button
      className={`flex-shrink-0 w-32 p-4 rounded-2xl border-2 transition-all ${
        isSelected
          ? 'border-purple-500 bg-purple-500/10'
          : 'border-white/10 bg-zinc-900'
      }`}
      onClick={onSelect}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${personality.color} flex items-center justify-center text-2xl mb-2 mx-auto`}>
        {personality.icon}
      </div>
      <h4 className="text-white text-sm font-semibold mb-1">{personality.name}</h4>
      <p className="text-gray-400 text-xs">{personality.description}</p>
    </motion.button>
  );
}

/**
 * SettingToggle - Toggle switch component
 */
function SettingToggle({ icon, label, description, enabled, onChange, loading, accentColor = 'purple' }) {
  const colorClasses = {
    purple: 'bg-purple-500',
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500'
  };

  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/50">
      <div className="flex items-center space-x-3">
        <div className={loading ? 'text-yellow-400 animate-pulse' : 'text-gray-400'}>{icon}</div>
        <div>
          <p className="text-white text-sm font-medium">{label}</p>
          {description && (
            <p className="text-gray-400 text-xs">{description}</p>
          )}
        </div>
      </div>
      <motion.button
        className={`relative w-12 h-7 rounded-full transition-colors ${
          loading ? 'bg-yellow-500 animate-pulse' :
          enabled ? colorClasses[accentColor] : 'bg-zinc-700'
        }`}
        onClick={() => !loading && onChange(!enabled)}
        whileTap={loading ? {} : { scale: 0.95 }}
        disabled={loading}
      >
        <motion.div
          className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-lg"
          animate={{ x: enabled || loading ? 25 : 2 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </motion.button>
    </div>
  );
}

/**
 * SettingItem - Clickable setting row
 */
function SettingItem({ icon, label, hasChevron, isDestructive, onClick }) {
  return (
    <motion.button
      className={`w-full flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors ${
        isDestructive ? 'text-red-500' : 'text-white'
      }`}
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="flex items-center space-x-3">
        <div className={isDestructive ? 'text-red-500' : 'text-gray-400'}>
          {icon}
        </div>
        <span className="text-sm font-medium">{label}</span>
      </div>
      {hasChevron && (
        <ChevronRight className="w-5 h-5 text-gray-600" />
      )}
    </motion.button>
  );
}
